import React, { Fragment } from "react";
import { toUSD } from "../../util/currency-formatter";
import idx from "idx";
import {
  addSubTasksToOptions,
  addSubTasksToSections,
} from "../../routes/projects/utils/append-subtask";
import { getFormatedData } from "../../routes/projects/task-list/task";
import { roundValue } from "src/util/decimal-utils";
/**
 * @description
 * This component renders a detailed breakdown of project stages, tasks, and subtasks
 * along with their respective costs. It also supports displaying variation data if
 * `isVariation` is true.
 *
 * @props
 * - optionsData: List of options or variations for the project.
 * - projectTotal: Total cost of the entire project.
 * - isVariation: Boolean flag to switch between standard and variation views.
 * - stageTotal: Summary of all project stages and tasks.
 * - optionTotal: Summary of all option or variation-related costs.
 * - subTaskList: List of subtasks used to enrich task and option details.
 * - selectedOptionId: Optional ID to filter and display a specific option/variation.
 * - viewType: String prop to toggle between 'full' and 'tasks_only' views.
 * - stages: List of stage objects with hierarchy information (id, parent_stage_id, name, etc.).
 *
 * @features
 * - Renders stages, tasks, and subtasks with cost breakdowns.
 * - Supports variations with revision handling.
 * - Outputs a final total row at the bottom.
 * - Handles nested stages/sub-stages recursively.
 *
 */
function DetailedProposal({
  optionsData,
  projectTotal,
  isVariation,
  stageTotal,
  optionTotal,
  subTaskList,
  selectedOptionId = "",
  viewType = "full",
  stages,
  breakupView = "detailed",
  projectType = "project",
}) {
  let updatedStageTotal = {};
  let updatedOptionTotal = {};

  if (!isVariation) {
    updatedStageTotal = addSubTasksToSections(stageTotal, subTaskList);
    updatedOptionTotal = addSubTasksToOptions(
      idx(optionTotal, (_) => _.stageSummary.projectlevel.optionList) || {},
      subTaskList
    );
  } else {
    // For variations, use the pre-enriched optionsData prop directly.
    if (Array.isArray(optionsData)) {
      optionsData.forEach((option) => {
        // The addSubTasksToOptions function is sometimes run upstream, and sometimes not.
        // To handle both cases, we ensure subtasks are correctly assigned here.
        const finalSubtasks =
          option.subTasks ||
          subTaskList.filter(
            (st) => st.taskId === option.id || st.optionId === option.id
          );
        updatedOptionTotal[option.id] = { ...option, subTasks: finalSubtasks };
      });
    }
  }

  if (selectedOptionId && updatedOptionTotal[selectedOptionId]) {
    updatedOptionTotal = {
      [selectedOptionId]: updatedOptionTotal[selectedOptionId],
    };
  }

  // flatten the stages
  const flattenStages = (stgs) => {
    let flat = [];
    const recurse = (stg) => {
      const stageCopy = { ...stg };
      const children = stageCopy.stages || [];
      delete stageCopy.stages;
      flat.push(stageCopy);
      children.forEach(recurse);
    };
    if (Array.isArray(stgs)) {
      stgs.forEach(recurse);
    } else if (stgs) {
      recurse(stgs);
    }
    return flat;
  };

  const flatStages = flattenStages(stages);

  // build the stage tree
  const buildStageTree = (allStages, parentId = null) => {
    return allStages
      .filter((s) => s.parent_stage_id === parentId)
      .map((s) => ({
        ...s,
        children: buildStageTree(allStages, s.id),
      }));
  };

  // render the stage
  const renderStage = (stage, level = 0) => {
    const section = updatedStageTotal[stage.id] || stageTotal[stage.id];
    if (!section) return [];

    const stagesCost = getFormatedData(section);
    const isTopLevel = level === 0;
    const stageClass = isTopLevel ? "btn-bg-brand-3 text-white" : "";
    const stagePadding = isTopLevel
      ? "ps-3"
      : `ps-${(level > 1 ? level + 1 : level) * 4}`;
    const noOfStages = stage?.stages?.length || 0;
    const stageTotalPadding =
      level <= 1
        ? "pe-2"
        : `pe-${
            (level > 1
              ? 1 + 2 // to make sure the total padding is 3 when level is more than 1
              : level + 2) * 7
          }`;

    let rows = [
      <tr key={stage.id} className="stage-row border-bottom-0">
        <td
          className={`text-left ${stagePadding} ${stageClass}`}
          data-cy="stage-name"
        >
          <b>{stage.name || section.stageHierarchy}</b>
        </td>
        <td className={stageClass}></td>
        <td className={stageClass}>
          <span
            className={`text-right ${stageTotalPadding}`}
            data-cy="stage-total"
          >
            {stagesCost.totalSaleAmount}
          </span>
        </td>
      </tr>,
    ];

    // Render child stages recursively
    if (stage.children && stage.children.length > 0) {
      stage.children.forEach((child) => {
        rows = rows.concat(renderStage(child, level + 1));
      });
    }

    // Render tasks under this stage conditionally
    if (
      section.taskList?.tasks &&
      (isVariation || breakupView === "detailed-item" || level === 0)
    ) {
      Object.entries(section.taskList.tasks).forEach(
        ([taskId, task], index) => {
          const taskCost = getFormatedData(task);
          const taskPadding =
            isTopLevel && noOfStages === 0
              ? "ps-3"
              : `ps-${(level > 1 ? level + 1 : level) * 5}`;
          const taskTotalPadding =
            isTopLevel && noOfStages === 0
              ? "pe-2"
              : `pe-${
                  (level > 1
                    ? 1 + 2 // to make sure the total padding is 3 when level is more than 1
                    : level + 2) * 7
                }`;

          rows.push(
            <tr key={taskId} className={`${index === 0 ? "border-top-0" : ""}`}>
              <td className={`text-left ${taskPadding}`} data-cy="task-name">
                <b>{task.name || `Task ${taskId}`}</b>
              </td>
              <td>{task.templateQuantity}</td>
              <td>
                <span
                  className={`text-right ${taskTotalPadding}`}
                  data-cy="task-total"
                >
                  {taskCost.totalSaleAmount}
                </span>
              </td>
            </tr>
          );

          // Comment out subtask rendering

          const subtaskTotalPadding =
            level < 0
              ? "pe-2"
              : `pe-${
                  (level > 1
                    ? 1 + 2 // to make sure the total padding is 3 when level is more than 1
                    : level + 2) *
                  (7 + 5)
                }`;

          if (
            task.subTasks &&
            (isVariation || breakupView === "detailed-item")
          ) {
            task.subTasks.forEach((subTask, subIndex) => {
              const subTaskPadding =
                level < 1
                  ? "ps-8"
                  : `ps-${(level > 1 ? level + 1 : level) * 5}`;
              if (viewType === "tasks_only") {
                rows.push(
                  <tr key={`${taskId}-sub-${subIndex} subtask`}>
                    <td className={`${subTaskPadding} text-left description`}>
                      {subTask.description}
                    </td>
                    <td className="text-center quantity">
                      {roundValue(subTask.templateQuantity || 1, 2)}
                    </td>
                    <td
                      className={`text-right total ${subtaskTotalPadding} total`}
                    >
                      <span
                        className={`text-right ${subtaskTotalPadding}`}
                        data-cy="subtask-total"
                      >
                        {toUSD(subTask.totalSaleAmount)}
                      </span>
                    </td>
                  </tr>
                );
              } else {
                rows.push(
                  <tr key={`${taskId}-sub-${subIndex}`} className="subtask">
                    <td className={`${subTaskPadding} text-left description`}>
                      {subTask.description}
                    </td>
                    <td className="text-center quantity ">
                      {roundValue(subTask.templateQuantity || 1, 2)}
                    </td>
                    <td
                      className={`text-right total ${subtaskTotalPadding} total`}
                    >
                      <span data-cy="subtask-total">
                        {toUSD(subTask.totalSaleAmount)}
                      </span>
                    </td>
                  </tr>
                );
                if (isVariation) {
                  rows = rows.concat(
                    renderItems(
                      subTask.material,
                      "Materials",
                      subTaskPadding
                    ) || []
                  );
                  rows = rows.concat(
                    renderItems(subTask.labor, "Resources", subTaskPadding) ||
                      []
                  );
                  rows = rows.concat(
                    renderItems(
                      subTask.subcontractor,
                      "Contractors",
                      subTaskPadding
                    ) || []
                  );
                }
              }
            });
          }
        }
      );
    }

    return rows;
  };

  // render the items
  const renderItems = (items, category) => {
    if (!items || items.length === 0) return null;

    const parsedItems = items
      .map((item) => {
        try {
          return typeof item === "string" ? JSON.parse(item) : item;
        } catch (e) {
          console.error("Failed to parse item:", item, e);
          return null;
        }
      })
      .filter((item) => item !== null);

    if (parsedItems.length === 0) return null;

    return (
      <>
        <tr>
          <td colSpan="3" className="ps-3 text-left">
            <b>{category}</b>
          </td>
        </tr>
        {parsedItems.map((item, index) => {
          let description = "N/A";
          let quantity = item.qty ? parseFloat(item.qty).toFixed(2) : 1; // Default to 1 and round to 2 decimal places
          let total = 0;

          if (category === "Materials") {
            description = item.item ? item.item.split("|")[0] : "N/A";
            total = item.total;
          } else if (category === "Resources") {
            description = item.name;
            total = item.total;
          } else if (category === "Contractors") {
            description = item.name;
            total = item.saleAmount;
          }

          return (
            <tr key={`${category}-${index}`}>
              <td className="ps-5 text-left">{description}</td>
              <td className="text-center">{quantity}</td>
              <td>
                <span className="text-right">{toUSD(total)}</span>
              </td>
            </tr>
          );
        })}
      </>
    );
  };

  // render the variation or option
  const variationJSX = () => {
    if (!updatedOptionTotal || Object.keys(updatedOptionTotal).length === 0)
      return null;

    return Object.entries(updatedOptionTotal).map(
      ([optionId, option], index) => {
        if (optionId === "projectTotal") return null;
        const { name = "", revisionName = "", revisionNumber = 0 } = option;
        const variationName =
          name +
          (revisionName && revisionNumber !== 1 ? " - " + revisionName : "");

        const subTaskContent =
          option.subTasks?.map((subTask, subIndex) => {
            if (viewType === "tasks_only") {
              return (
                <tr key={subIndex} className="subtask">
                  <td
                    colSpan={isVariation ? 3 : ""}
                    className={`${
                      isVariation ? "ps-3" : "ps-15"
                    } text-left description`}
                  >
                    {subTask.description}
                  </td>
                  {!isVariation && (
                    <>
                      <td className="text-center quantity">
                        {roundValue(subTask.templateQuantity, 2)}
                      </td>
                      <td className="text-right total pe-15">
                        <span>{toUSD(subTask.totalSaleAmount)}</span>
                      </td>
                    </>
                  )}
                </tr>
              );
            }
            return (
              <Fragment key={subIndex}>
                <tr className="subtask">
                  <td
                    colSpan={isVariation ? 3 : ""}
                    className={`${
                      isVariation ? "ps-3" : "ps-5"
                    } text-left description`}
                  >
                    {subTask.description}
                  </td>
                  {!isVariation && breakupView === "detailed-item" && (
                    <>
                      <td className="text-center quantity">
                        {roundValue(subTask.templateQuantity || 1, 2)}
                      </td>
                      <td className="text-right total pe-15">
                        <span>{toUSD(subTask.totalSaleAmount)}</span>
                      </td>
                    </>
                  )}
                </tr>
                {isVariation &&
                  renderItems(subTask.material, "Materials", "ps-3")}
                {isVariation && renderItems(subTask.labor, "Resources", "ps-3")}
                {isVariation &&
                  renderItems(subTask.subcontractor, "Contractors", "ps-3")}
              </Fragment>
            );
          }) || [];

        const optionSaleAmount =
          option?.totalSaleAmount ??
          idx(
            optionTotal,
            (_) => _.stageSummary.projectlevel.totalSaleAmount
          ) ??
          0;

        if ((viewType === "tasks_only" || viewType === "full") && isVariation) {
          return subTaskContent;
        }

        return (
          <Fragment key={index}>
            <tr>
              <td
                className={`text-left ${isVariation ? "" : "ps-3"}`}
                data-cy="variation-name"
              >
                {variationName}
              </td>
              <td></td>
              <td>
                <span
                  className={`text-right ${isVariation ? "" : "pe-2"}`}
                  data-cy="variation-total"
                >
                  {toUSD(optionSaleAmount)}
                </span>
              </td>
            </tr>
            {(isVariation || breakupView === "detailed-item") && subTaskContent}{" "}
            {/* only render subtask content if it's a variation */}
          </Fragment>
        );
      }
    );
  };

  // render the variations data or option data
  const viewVariationsData = () => {
    return (
      <>
        {!isVariation && (
          <div className="mt-2">
            <div className="ps-2 text-left" colSpan={2}>
              {projectType === "PROJECT" ? "Variations: " : "Options: "}
            </div>
          </div>
        )}
        {variationJSX({ optionsData })}
        {/* only render the option total if it's not a variation */}
        {!isVariation && (
          <tr>
            <td colSpan={2} className="text-right">
              <span className="tableinfo" data-cy="option-total">
                Total
              </span>
            </td>
            <td>
              <span
                className="tableinfo text-right pe-2"
                data-cy="option-total"
              >
                {toUSD(optionTotal.stageSummary?.projectlevel?.totalSaleAmount)}
              </span>
            </td>
          </tr>
        )}
      </>
    );
  };

  const variationTotal = isVariation
    ? getFormatedData(idx(optionTotal, (_) => _.projectTotal) || {})
    : getFormatedData(idx(projectTotal, (_) => _) || {});
  const stageTree = buildStageTree(flatStages);

  return (
    <div
      className="col-12 my-3 table-responsive detailed-proposal"
      data-cy="detailed-proposal"
    >
      <table
        className={`table-create mb-0 table table-bordered fw-bolder ${
          isVariation ? "table-row-color" : ""
        }`}
      >
        <thead>
          <tr>
            <th
              className={`w-auto text-left summary-header-row ${
                isVariation ? "" : "ps-3"
              }`}
              data-cy="description"
            >
              Description
            </th>
            {isVariation || breakupView === "detailed-item" ? (
              <th className={`text-center summary-header-row`}>Quantity</th>
            ) : (
              <th className={`text-center summary-header-row`}></th>
            )}
            <th
              className={`text-right summary-header-row ${
                isVariation ? "" : "pe-5"
              }`}
            >
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {stageTree.map((stage) => renderStage(stage))}
          {isVariation && viewVariationsData()}
          <tr>
            <td colSpan={2} className="text-right">
              <span className="tableinfo" data-cy="variation-total">
                Total
              </span>
            </td>
            <td>
              <span
                className={`tableinfo text-right ${isVariation ? "" : "pe-2"}`}
                data-cy="variation-total"
              >
                {variationTotal?.totalSaleAmount}
              </span>
            </td>
          </tr>
          {/* only render variations data if it's not a variation and there are options */}
          {(!isVariation &&
            Array.isArray(optionsData) &&
            optionsData.length > 0 &&
            viewVariationsData()) ||
            ""}
        </tbody>
      </table>
    </div>
  );
}

export default DetailedProposal;
